// src/components/discover/SwipeCard.tsx
//
// A single Tinder-style card. Pointer-event driven (unified touch + mouse).
// Uses CSS transforms for real-time drag feedback and spring-back, plus
// a fly-away transition on commit. Zero external animation libs.
//
// Gestures:
//   Swipe right → match
//   Swipe left  → pass
//   Swipe up    → super-match
//   (down is intentionally unmapped)
//
// Thresholds: ~25% of card width for horizontal, ~15% of viewport height
// for vertical. Cards under the threshold spring back.

import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Box, Card, Typography, Chip, IconButton } from '@mui/material';
import {
  FavoriteOutlined,
  CloseOutlined,
  StarOutlined,
  ChatOutlined,
  Star,
  StarHalf,
  StarBorder,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import type { FeedItem, SwipeAction } from '../../hooks/useFeed';
import { MiniMap } from '../map/MiniMap';
import { useT } from '../../i18n/useT';

interface SwipeCardProps {
  item: FeedItem;
  /** Called when the user commits a swipe. */
  onSwipe: (action: SwipeAction) => void;
  /** Tap on the text/body of the card — opens detail sheet for the item. */
  onTap: () => void;
  /** Depth from the top of the stack. 0 = top. Used to fan out behind. */
  depth: number;
  /** Total cards visible in stack — used for subtle scaling of back cards. */
  stackSize: number;
}

const SWIPE_H_THRESHOLD = 90; // px beyond which it counts as a swipe
const SWIPE_V_THRESHOLD = 120;
const ROTATION_FACTOR = 0.08; // deg per px of horizontal drag
const FLY_DISTANCE = 1200; // px the card travels when flying off
const TAP_MAX_MOVEMENT = 8; // px. Below this a release is a tap, not a swipe

export const SwipeCard: React.FC<SwipeCardProps> = ({
  item,
  onSwipe,
  onTap,
  depth,
  stackSize,
}) => {
  const t = useT();
  const cardRef = useRef<HTMLDivElement>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  // Track the DOM target the user pressed down on, so we can branch
  // tap behavior (image area vs body area) without traversing the
  // event tree at release time.
  const startedOnImageArea = useRef(false);
  const imageRectAtStart = useRef<DOMRect | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isFlying, setIsFlying] = useState<SwipeAction | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Photo carousel index. Bounded to photos.length below.
  const [photoIdx, setPhotoIdx] = useState(0);

  // Photos to render. Prefer multi-photo carousel from Place Details;
  // fall back to single image_url; empty for knowledge picks.
  const photos = useMemo(() => {
    if (item.origin === 'llm-knowledge') return [];
    if (item.photo_urls && item.photo_urls.length > 0) {
      return item.photo_urls;
    }
    if (item.image_url) return [item.image_url];
    return [];
  }, [item.photo_urls, item.image_url, item.origin]);

  const currentPhoto = photos[Math.min(photoIdx, photos.length - 1)];

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (depth > 0) return; // only top card responds
    if (isFlying) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    startPos.current = { x: e.clientX, y: e.clientY };

    // Detect whether the press began inside the image area, so that
    // a quick tap (no drag) can paginate the photo carousel rather
    // than open the detail sheet. We walk up from the actual target
    // looking for our marked element.
    let node: Element | null = e.target as Element;
    let onImage = false;
    while (node && node !== e.currentTarget) {
      if (node.getAttribute && node.getAttribute('data-card-zone') === 'image') {
        onImage = true;
        imageRectAtStart.current = (node as HTMLElement).getBoundingClientRect();
        break;
      }
      node = node.parentElement;
    }
    startedOnImageArea.current = onImage;

    setIsDragging(true);
  }, [depth, isFlying]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !startPos.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    setDragOffset({ x: dx, y: dy });
  }, [isDragging]);

  const commitFlyAway = useCallback((action: SwipeAction) => {
    setIsFlying(action);
    // After the CSS transition completes, notify parent
    setTimeout(() => {
      onSwipe(action);
    }, 280);
  }, [onSwipe]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !startPos.current) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);

    const dx = dragOffset.x;
    const dy = dragOffset.y;
    const totalMovement = Math.hypot(dx, dy);
    const startedOnImage = startedOnImageArea.current;
    const imageRect = imageRectAtStart.current;
    // Where the user actually released — used for image-area
    // tap-to-paginate (left half = back, right half = forward).
    const releaseX = e.clientX;

    setIsDragging(false);
    startPos.current = null;
    startedOnImageArea.current = false;
    imageRectAtStart.current = null;

    if (totalMovement < TAP_MAX_MOVEMENT) {
      // Quick tap — branch by zone
      setDragOffset({ x: 0, y: 0 });

      if (startedOnImage && imageRect && photos.length > 1) {
        // Image area tap → paginate the carousel
        const midX = imageRect.left + imageRect.width / 2;
        if (releaseX < midX) {
          setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
        } else {
          setPhotoIdx((i) => (i + 1) % photos.length);
        }
        return;
      }
      // Body tap → open detail sheet
      onTap();
      return;
    }

    if (dy < -SWIPE_V_THRESHOLD && Math.abs(dy) > Math.abs(dx)) {
      commitFlyAway('super_match');
    } else if (dx > SWIPE_H_THRESHOLD) {
      commitFlyAway('match');
    } else if (dx < -SWIPE_H_THRESHOLD) {
      commitFlyAway('pass');
    } else {
      // Spring back
      setDragOffset({ x: 0, y: 0 });
    }
  }, [isDragging, dragOffset, commitFlyAway, onTap, photos.length]);

  // Compute transform
  let tx = dragOffset.x;
  let ty = dragOffset.y;
  let rot = dragOffset.x * ROTATION_FACTOR;

  if (isFlying === 'match') {
    tx = FLY_DISTANCE;
    rot = 30;
  } else if (isFlying === 'pass') {
    tx = -FLY_DISTANCE;
    rot = -30;
  } else if (isFlying === 'super_match') {
    ty = -FLY_DISTANCE;
    rot = 0;
  }

  // Cards behind the top get a slight scale and Y-offset for depth
  const behindScale = 1 - depth * 0.04;
  const behindY = depth * 8;

  // Opacity hints for swipe direction (only on top card mid-drag)
  const matchOpacity =
    depth === 0 ? Math.min(1, Math.max(0, dragOffset.x / SWIPE_H_THRESHOLD)) : 0;
  const passOpacity =
    depth === 0 ? Math.min(1, Math.max(0, -dragOffset.x / SWIPE_H_THRESHOLD)) : 0;
  const superOpacity =
    depth === 0
      ? Math.min(1, Math.max(0, -dragOffset.y / SWIPE_V_THRESHOLD))
      : 0;

  return (
    <Box
      ref={cardRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      sx={{
        position: 'absolute',
        inset: 0,
        transform: `translate(${tx}px, ${ty + behindY}px) rotate(${rot}deg) scale(${behindScale})`,
        transition: isFlying
          ? 'transform 0.28s cubic-bezier(0.55, 0.085, 0.68, 0.53)'
          : isDragging
          ? 'none'
          : 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        touchAction: depth === 0 ? 'none' : 'auto',
        cursor: depth === 0 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        userSelect: 'none',
        zIndex: 10 - depth,
        pointerEvents: depth === 0 ? 'auto' : 'none',
      }}
    >
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          // Was 3 (24px) — too rounded compared to the rest of the
          // app's 16px cards, which made the contained MiniMap show
          // a visible corner curve at the top. Matches Guide and
          // Postcard card radii now.
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: depth === 0 ? 4 : 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          position: 'relative',
        }}
      >
        {/* Notification badge for items surfaced via the bell */}
        {item.is_notification && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              px: 1.25,
              py: 0.25,
              borderRadius: 2,
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              zIndex: 2,
            }}
          >
            
            {t("⚡ For you")}
          </Box>
        )}

        {/* Swipe-direction stamps (visible only on drag) */}
        <SwipeStamp label={t("LIKE")} color="#4ade80" opacity={matchOpacity} align="left" rotate={-15} />
        <SwipeStamp label={t("PASS")} color="#f87171" opacity={passOpacity} align="right" rotate={15} />
        <SwipeStamp
          label={t("SUPER")}
          color="#60a5fa"
          opacity={superOpacity}
          align="center"
          rotate={0}
        />

        {/* Image area — carousel of up to 5 Place Photos when
            available; yellow "Local pick" placeholder for AI
            knowledge picks; generic 📍 fallback when nothing rendered.
            data-card-zone tells the pointer handler this is the
            image region (so a quick tap paginates rather than
            opening the detail sheet). */}
        {currentPhoto ? (
          <Box
            data-card-zone="image"
            sx={{
              width: '100%',
              aspectRatio: '16/10',
              position: 'relative',
              backgroundImage: `url(${currentPhoto})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              bgcolor: 'action.hover',
              flexShrink: 0,
              userSelect: 'none',
            }}
          >
            {/* Dot indicator (only when multiple photos) */}
            {photos.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 0.5,
                  px: 2,
                }}
              >
                {photos.map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: '1 1 0',
                      maxWidth: 40,
                      height: 3,
                      borderRadius: 2,
                      bgcolor:
                        i === photoIdx
                          ? 'rgba(255,255,255,0.95)'
                          : 'rgba(255,255,255,0.4)',
                      transition: 'background-color 0.2s ease',
                      boxShadow: '0 0 2px rgba(0,0,0,0.4)',
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Chevron buttons (desktop hover affordance + mobile tap target).
                Pointer events handled by the underlying card swipe — we
                stop propagation here so a click directly on the chevron
                still paginates without committing the card swipe. */}
            {photos.length > 1 && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoIdx((i) => (i - 1 + photos.length) % photos.length);
                  }}
                  sx={{
                    position: 'absolute',
                    left: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                    width: 32,
                    height: 32,
                  }}
                  aria-label={t("Previous photo")}
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoIdx((i) => (i + 1) % photos.length);
                  }}
                  sx={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                    width: 32,
                    height: 32,
                  }}
                  aria-label={t("Next photo")}
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        ) : (() => {
          // No photos available. Use MiniMap when we have valid
          // destination coords — gives the user a sense of place,
          // a live walking route from their current position, and
          // is far more useful than a generic emoji placeholder.
          // Falls back to the original gradient if coords are missing
          // or the Maps SDK is unavailable.
          const hasCoords =
            typeof item.location?.lat === 'number' &&
            typeof item.location?.lng === 'number' &&
            !(item.location.lat === 0 && item.location.lng === 0);
          if (!hasCoords) {
            return (
              <Box
                data-card-zone="image"
                sx={{
                  width: '100%',
                  aspectRatio: '16/10',
                  background:
                    item.origin === 'llm-knowledge'
                      ? 'linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)'
                      : 'linear-gradient(135deg, #f5f3f0 0%, #e6dfd3 100%)',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 56, opacity: 0.4 }}>
                  {item.origin === 'llm-knowledge' ? '🗒️' : '📍'}
                </Typography>
                {item.origin === 'llm-knowledge' && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'rgba(120,53,15,0.8)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: 10,
                    }}
                  >
                    
                    {t("Local pick")}
                  </Typography>
                )}
              </Box>
            );
          }
          return (
            <Box
              data-card-zone="image"
              sx={{
                width: '100%',
                aspectRatio: '16/10',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              <MiniMap
                destination={{
                  lat: item.location.lat,
                  lng: item.location.lng,
                  name: item.title,
                }}
                active={depth === 0}
                depth={depth}
                aspectRatio="16/10"
                interactive={false}
              />
              {item.origin === 'llm-knowledge' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'rgba(252, 211, 77, 0.95)',
                    color: 'rgba(120,53,15,0.9)',
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  
                  {t("Local pick")}
                </Box>
              )}
            </Box>
          );
        })()}

        {/* Card content */}
        <Box sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.75 }}
          >
            {item.title}
          </Typography>

          {/* Ratings + price + open-status strip. Renders only the
              pieces we actually have data for, so listings without
              Place Details don't show empty pills. */}
          {(item.rating !== undefined ||
            item.price_level !== undefined ||
            item.is_open_now !== undefined) && (
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              {item.rating !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <StarsRow rating={item.rating} />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, ml: 0.5 }}
                  >
                    {item.rating.toFixed(1)}
                  </Typography>
                  {item.user_ratings_total !== undefined && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.25 }}
                    >
                      ({formatRatingsCount(item.user_ratings_total)})
                    </Typography>
                  )}
                </Box>
              )}
              {item.price_level !== undefined && item.price_level > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    letterSpacing: 1,
                  }}
                >
                  {'$'.repeat(Math.min(4, item.price_level))}
                  <span style={{ opacity: 0.3 }}>
                    {'$'.repeat(Math.max(0, 4 - item.price_level))}
                  </span>
                </Typography>
              )}
              {item.is_open_now !== undefined && (
                <Chip
                  label={item.is_open_now ? 'Open now' : 'Closed'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    bgcolor: item.is_open_now
                      ? 'success.light'
                      : 'action.disabledBackground',
                    color: item.is_open_now ? 'success.dark' : 'text.secondary',
                  }}
                />
              )}
            </Box>
          )}

          {(item.distance_m !== undefined || item.walk_time_min !== undefined) && (
            <Box sx={{ mb: 1, display: 'flex', gap: 1.5 }}>
              {item.distance_m !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  {item.distance_m < 1000
                    ? `${item.distance_m}m`
                    : `${(item.distance_m / 1000).toFixed(1)}km`}
                </Typography>
              )}
              {item.walk_time_min !== undefined && (
                <Typography variant="caption" color="text.secondary">
                  · {item.walk_time_min}  {t("min walk")}
                </Typography>
              )}
            </Box>
          )}

          <Typography
            variant="body2"
            color="text.primary"
            sx={{ lineHeight: 1.5, mb: 1.5 }}
          >
            {item.description}
          </Typography>

          {item.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
              {item.tags.slice(0, 5).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, fontSize: '0.72rem' }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Footer: attribution when we have a Place ID, otherwise
              the original tap hint. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 'auto', fontSize: '0.66rem', opacity: 0.75 }}
          >
            {item.place_id
              ? 'Info from Google Places'
              : item.origin === 'llm-knowledge'
              ? 'Curated pick · tap for details'
              : 'Tap for details, swipe to decide'}
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

/** Compact "1.2k" / "3.4M" formatter for review counts. */
function formatRatingsCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1000000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1000000).toFixed(1)}M`;
}

/** Five-star row with half-star precision. */
const StarsRow: React.FC<{ rating: number }> = ({ rating }) => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const fullCount = rating - full >= 0.75 ? full + 1 : full;
  const finalFull = hasHalf ? full : fullCount;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {Array.from({ length: finalFull }, (_, i) => (
        <Star key={`f${i}`} sx={{ fontSize: 14, color: '#f59e0b' }} />
      ))}
      {hasHalf && <StarHalf sx={{ fontSize: 14, color: '#f59e0b' }} />}
      {Array.from(
        { length: 5 - finalFull - (hasHalf ? 1 : 0) },
        (_, i) => (
          <StarBorder key={`e${i}`} sx={{ fontSize: 14, color: '#f59e0b' }} />
        )
      )}
    </Box>
  );
};

/**
 * Action buttons below the card. Separate component so they don't
 * participate in the drag gesture.
 */
export const SwipeCardActions: React.FC<{
  onPass: () => void;
  onSuperMatch: () => void;
  onMatch: () => void;
  onChat: () => void;
  disabled?: boolean;
}> = ({ onPass, onSuperMatch, onMatch, onChat, disabled }) => {
  const t = useT();
  return (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 1.25,
    }}
  >
    <ActionButton
      label={t("Pass")}
      icon={<CloseOutlined sx={{ fontSize: 22 }} />}
      color="#f87171"
      onClick={onPass}
      disabled={disabled}
      size={44}
    />
    <ActionButton
      label={t("Chat")}
      icon={<ChatOutlined sx={{ fontSize: 18 }} />}
      color="#a78bfa"
      onClick={onChat}
      disabled={disabled}
      size={36}
    />
    <ActionButton
      label={t("Super")}
      icon={<StarOutlined sx={{ fontSize: 20 }} />}
      color="#60a5fa"
      onClick={onSuperMatch}
      disabled={disabled}
      size={40}
    />
    <ActionButton
      label={t("Like")}
      icon={<FavoriteOutlined sx={{ fontSize: 24 }} />}
      color="#4ade80"
      onClick={onMatch}
      disabled={disabled}
      size={50}
    />
  </Box>
  );
};

const ActionButton: React.FC<{
  label: string;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
  disabled?: boolean;
  size: number;
}> = ({ label, icon, color, onClick, disabled, size }) => (
  <IconButton
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    sx={{
      width: size,
      height: size,
      border: '2px solid',
      borderColor: color,
      color,
      bgcolor: 'background.paper',
      transition: 'transform 0.15s, background-color 0.15s',
      '&:hover': {
        bgcolor: `${color}22`,
        transform: 'scale(1.05)',
      },
      '&:disabled': {
        opacity: 0.4,
      },
    }}
  >
    {icon}
  </IconButton>
);

const SwipeStamp: React.FC<{
  label: string;
  color: string;
  opacity: number;
  align: 'left' | 'right' | 'center';
  rotate: number;
}> = ({ label, color, opacity, align, rotate }) => {
  const t = useT();
  const positioning: Record<string, any> =
    align === 'center'
      ? { top: '30%', left: '50%', transform: `translateX(-50%) rotate(${rotate}deg)` }
      : align === 'left'
      ? { top: 24, left: 24, transform: `rotate(${rotate}deg)` }
      : { top: 24, right: 24, transform: `rotate(${rotate}deg)` };

  return (
    <Box
      sx={{
        position: 'absolute',
        ...positioning,
        pointerEvents: 'none',
        border: `3px solid ${color}`,
        color,
        px: 2,
        py: 0.5,
        borderRadius: 1.5,
        fontWeight: 800,
        letterSpacing: 2,
        fontSize: '1.2rem',
        opacity,
        transition: 'opacity 0.12s',
        zIndex: 2,
      }}
    >
      {label}
    </Box>
  );
};
